import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useData } from "../../Contexts/DataContext";
import { v4 as uuidv4 } from "uuid";

const FormField = ({
  label,
  name,
  value,
  onChange,
  isRequired = false,
  type = "text",
  as = Input,
}) => (
  <FormControl isRequired={isRequired}>
    <FormLabel fontWeight={"bold"}>{label}</FormLabel>
    {as === Textarea ? (
      <Textarea
        name={name}
        value={value}
        onChange={onChange}
        bg={"white"}
        size="sm"
        fontSize="sm"
        borderRadius={"md"}
      />
    ) : (
      <Input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        bg={"white"}
        size="sm"
        fontSize="sm"
        borderRadius={"md"}
      />
    )}
  </FormControl>
);

function AddEditSOWModal({ isOpen, onClose, sow = null }) {
  const { clients, sows, addDocument, updateDocument } = useData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const toast = useToast();

  const filteredSows = sows.filter((sow) => sow.clientId === selectedClientId);

  const initialSowData = {
    id: null,
    clientId: "",
    projectName: "",
    startDate: "",
    endDate: "",
    scopeOfWork: "",
    assumptions: "",
    outOfScope: "",
    changeControl: "",
    payment: "",
    objectives: [""],
    deliverables: [{ id: uuidv4(), name: "", targetDate: "", assigned: "" }],
  };

  const [sowData, setSowData] = useState(initialSowData);

  useEffect(() => {
    if (isOpen) {
      if (sow) {
        setSowData({
          id: sow.id || null,
          clientId: sow.clientId || "",
          projectName: sow.projectName || "",
          startDate: sow.startDate || "",
          endDate: sow.endDate || "",
          scopeOfWork: sow.scopeOfWork || "",
          assumptions: sow.assumptions || "",
          outOfScope: sow.outOfScope || "",
          changeControl: sow.changeControl || "",
          payment: sow.payment || "",
          objectives: sow.objectives?.length ? sow.objectives : [""],
          deliverables: sow.deliverables?.length
            ? sow.deliverables
            : [{ id: uuidv4(), name: "", targetDate: "", assigned: "" }],
        });
        setSelectedClientId(sow.clientId || "");
      } else {
        setSowData(initialSowData);
        setSelectedClientId("");
      }
    }
  }, [isOpen, sow]);

  const requiredFields = ["projectName", "startDate", "endDate", "scopeOfWork"];

  useEffect(() => {
    const areRequiredFieldsFilled = requiredFields.every(
      (field) => sowData[field]?.trim() !== ""
    );
    const areObjectivesValid = sowData.objectives.some(
      (obj) => obj.trim() !== ""
    );
    const areDeliverablesValid = sowData.deliverables.some(
      (d) => d.name.trim() !== ""
    );

    setIsFormValid(
      areRequiredFieldsFilled && areObjectivesValid && areDeliverablesValid
    );
  }, [sowData]);

  const handleSowSelect = (sowId) => {
    const selected = sows.find((s) => s.id === sowId);
    if (selected) {
      setSowData({
        ...selected,
        objectives: selected.objectives || [""],
        deliverables:
          selected.deliverables?.length > 0
            ? selected.deliverables
            : [{ id: uuidv4(), name: "", targetDate: "", assigned: "" }],
      });
    }
  };

  const handleSowInputChange = (e) => {
    const { name, value } = e.target;
    setSowData({ ...sowData, [name]: value });
  };

  const handleDeliverableChange = (index, field, value) => {
    const newDeliverables = [...sowData.deliverables];
    newDeliverables[index][field] = value;
    if (index === newDeliverables.length - 1 && value.trim() !== "") {
      newDeliverables.push({
        id: uuidv4(),
        name: "",
        targetDate: "",
        assigned: "",
      });
    }
    setSowData({ ...sowData, deliverables: newDeliverables });
  };

  const removeDeliverable = (index) => {
    const newDeliverables = sowData.deliverables.filter((_, i) => i !== index);
    setSowData({ ...sowData, deliverables: newDeliverables });
  };

  const handleObjectiveChange = (index, value) => {
    const newObjectives = [...sowData.objectives];
    newObjectives[index] = value;
    if (index === newObjectives.length - 1 && value.trim() !== "") {
      newObjectives.push("");
    }
    setSowData({ ...sowData, objectives: newObjectives });
  };

  const removeObjective = (index) => {
    const newObjectives = sowData.objectives.filter((EASY, i) => i !== index);
    setSowData({ ...sowData, objectives: newObjectives });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !selectedClientId) return;
    setIsSubmitting(true);

    const cleanedObjectives = sowData.objectives.filter(
      (obj) => obj.trim() !== ""
    );
    const cleanedDeliverables = sowData.deliverables.filter(
      (d) => d.name.trim() !== ""
    );

    const { id, ...sowDataWithoutId } = sowData;

    const updatedSowData = {
      ...sowDataWithoutId,
      clientId: selectedClientId,
      status: "active",
      objectives: cleanedObjectives,
      deliverables: cleanedDeliverables,
    };

    try {
      if (sowData.id) {
        await updateDocument("sows", sowData.id, updatedSowData);
      } else {
        const newId = await addDocument("sows", updatedSowData);
        if (!newId) throw new Error("Failed to create new SOW");
      }

      toast({
        title: sowData.id ? "SOW Updated" : "SOW Created",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error("Error saving SOW:", error);
      toast({
        title: "Error saving SOW",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const keyFields = [
    { label: "Project Name", name: "projectName", isRequired: true },
  ];
  const dateFields = [
    { label: "Start Date", name: "startDate", isRequired: true, type: "date" },
    { label: "End Date", name: "endDate", isRequired: true, type: "date" },
  ];

  const formFields = [
    {
      label: "Scope of Work",
      name: "scopeOfWork",
      isRequired: true,
      as: Textarea,
    },
    { label: "Out of Scope", name: "outOfScope", as: Textarea },
    { label: "Payment Schedule", name: "payment", as: Textarea },
    { label: "Change Control Process", name: "changeControl", as: Textarea },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"xl"}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW={"container.xl"} bg={"gray.50"}>
        <ModalHeader bg={"gray.700"} color={"white"}>
          <VStack align="start" spacing={2}>
            <Text fontWeight="bold" fontSize="lg">
              {sowData.id ? "Edit SOW" : "Create SOW"}
            </Text>

            <HStack w="100%" spacing={4}>
              <FormControl isRequired>
                <FormLabel mb={0} fontSize="xs" color="white">
                  Client
                </FormLabel>
                <Select
                  bg="white"
                  color="black"
                  size="sm"
                  fontSize="sm"
                  borderRadius="md"
                  placeholder="Select client"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                >
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {!sow && selectedClientId && filteredSows.length > 0 && (
                <FormControl>
                  <FormLabel mb={0} fontSize="xs" color="white">
                    Load Existing SOW
                  </FormLabel>

                  <Select
                    size="sm"
                    bg="white"
                    color="black"
                    fontSize="sm"
                    borderRadius="md"
                    placeholder="Edit existing SOW"
                    onChange={(e) => handleSowSelect(e.target.value)}
                  >
                    {filteredSows.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.projectName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </HStack>
          </VStack>
        </ModalHeader>

        <ModalCloseButton color={"white"} />
        <ModalBody maxH={"70vh"} overflowY={"auto"}>
          <HStack align={"stretch"} spacing={4} mt={4}>
            <VStack spacing={4} align="stretch" flex={1}>
              <HStack>
                {keyFields.map((field) => (
                  <FormField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    value={sowData[field.name]}
                    onChange={handleSowInputChange}
                    isRequired={field.isRequired}
                    type={field.type}
                  />
                ))}
              </HStack>
              <HStack>
                {dateFields.map((field) => (
                  <FormField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    value={sowData[field.name]}
                    onChange={handleSowInputChange}
                    isRequired={field.isRequired}
                    type={field.type}
                  />
                ))}
              </HStack>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontWeight={"bold"}>Objectives</FormLabel>
                  <VStack spacing={2} align="stretch">
                    {sowData.objectives.map((objective, index) => (
                      <HStack key={index} width="100%">
                        <Input
                          bg={"white"}
                          size="sm"
                          fontSize="sm"
                          borderRadius={"md"}
                          value={objective}
                          placeholder="Add new objective"
                          onChange={(e) =>
                            handleObjectiveChange(index, e.target.value)
                          }
                        />
                        {index !== sowData.objectives.length - 1 && (
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => removeObjective(index)}
                          />
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </FormControl>
              </VStack>
              {formFields.map((field) => (
                <FormField
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  value={sowData[field.name]}
                  onChange={handleSowInputChange}
                  isRequired={field.isRequired}
                  as={field.as || Input}
                />
              ))}
            </VStack>

            <VStack spacing={0} align="stretch" flex={1} overflowY={"auto"}>
              <FormControl isRequired>
                <FormLabel fontWeight={"bold"}>Deliverables</FormLabel>
                <VStack spacing={4} align="stretch" w="100%" flex={1}>
                  {sowData.deliverables.map((deliverable, index) => (
                    <HStack
                      key={deliverable.id} // Changed from index to deliverable.id
                      w="100%"
                      flex={1}
                      spacing={3}
                      align="stretch"
                    >
                      <Box
                        p={3}
                        borderColor="gray.200"
                        borderWidth={1}
                        borderRadius="md"
                        w="100%"
                        flex={1}
                      >
                        <HStack w="100%" spacing={3} align="stretch">
                          <Textarea
                            bg="white"
                            size="sm"
                            fontSize="sm"
                            borderRadius="md"
                            placeholder="Deliverable Name"
                            value={deliverable.name}
                            onChange={(e) =>
                              handleDeliverableChange(
                                index,
                                "name",
                                e.target.value
                              )
                            }
                            flex={1}
                          />
                          <VStack minW="200px" align="stretch">
                            <FormControl>
                              <Input
                                bg="white"
                                size="sm"
                                fontSize="sm"
                                borderRadius="md"
                                type="date"
                                value={deliverable.targetDate}
                                onChange={(e) =>
                                  handleDeliverableChange(
                                    index,
                                    "targetDate",
                                    e.target.value
                                  )
                                }
                              />
                            </FormControl>
                            <FormControl>
                              <Input
                                bg="white"
                                size="sm"
                                fontSize="sm"
                                borderRadius="md"
                                placeholder="Consultant Name"
                                value={deliverable.assigned}
                                onChange={(e) =>
                                  handleDeliverableChange(
                                    index,
                                    "assigned",
                                    e.target.value
                                  )
                                }
                              />
                            </FormControl>
                          </VStack>
                        </HStack>
                      </Box>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={() => removeDeliverable(index)}
                        isDisabled={index === sowData.deliverables.length - 1}
                      />
                    </HStack>
                  ))}
                </VStack>
              </FormControl>
            </VStack>
          </HStack>
        </ModalBody>
        <Divider />
        <ModalFooter>
          <Button
            mr={3}
            colorScheme="green"
            onClick={handleSubmit}
            type="submit"
            isDisabled={!isFormValid || isSubmitting}
            isLoading={isSubmitting}
          >
            {sowData.id ? "Update SOW" : "Create SOW"}
          </Button>
          <Button variant={"outline"} colorScheme="gray" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddEditSOWModal;
